/*
 * kitsters-favicon-cart-count.js — badge the browser-tab favicon with the cart
 * item count.
 *
 * Independent implementation, not a port: India's equivalent is a Kalles custom
 * element (<favicon-cart-badge>) and the Kalles licence does not cover this
 * store (root CLAUDE.md §0.5 rule 2). Canvas + <link rel="icon"> only.
 *
 * Event contract is the theme's existing one, deliberately identical to
 * assets/kitsters-header.js so the badge and the header pill can never disagree:
 *   - `cart:count:update` — authoritative, carries { item_count } (§10.21)
 *   - `cart:update`       — carries {cartData, source, actionAfterATC} and NO
 *                           count, so read one only if genuinely present and
 *                           otherwise ask /cart.js. Never default a count to 0
 *                           (§10.21: that `?? 0` is what blanked the header
 *                           badge on every add-to-cart).
 */
(function () {
  'use strict';

  var cfg;
  try {
    var node = document.getElementById('kitsters-favicon-cart-data');
    if (!node) return;
    cfg = JSON.parse(node.textContent);
  } catch (e) {
    return;
  }
  if (!cfg || !cfg.src) return;

  var SIZE = 64;               // draw at 64 and let the browser downscale to 16/32
  var BADGE_BG = '#ff4683';    // brand pink
  // White on the pink disc, by owner request (2026-08-30). Noting the trade-off
  // rather than hiding it: white on #ff4683 is 3.25:1 and the black it replaced
  // was 6.09:1. WCAG text ratios are the wrong lens for a 16px tab glyph nobody
  // reads as prose — this is an icon — but if the digit ever looks washed out at
  // small sizes, that number is why. The ring below stays black, which is what
  // gives the disc its edge against a light tab strip.
  var BADGE_FG = '#ffffff';
  var BADGE_RING = '#0a0a0a';

  var originalHref = null;
  var baseImage = null;
  var disabled = false;        // set once a draw proves impossible; never retried
  var lastRendered = null;

  function iconLinks() {
    return Array.prototype.slice.call(
      document.querySelectorAll('link[rel~="icon"]')
    );
  }

  /*
   * Replace the node rather than mutating href. Safari does not reliably repaint
   * the tab on an href change to an existing <link rel="icon">, and a stale tab
   * icon is indistinguishable from "the script never ran".
   */
  function setIcon(href) {
    var links = iconLinks();
    if (originalHref === null) {
      originalHref = links.length ? links[0].getAttribute('href') : cfg.src;
    }
    links.forEach(function (l) { l.parentNode.removeChild(l); });
    var link = document.createElement('link');
    link.rel = 'icon';
    link.type = href.indexOf('data:image/png') === 0 ? 'image/png' : (links[0] && links[0].type) || 'image/png';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadBase() {
    return new Promise(function (resolve, reject) {
      if (baseImage) { resolve(baseImage); return; }
      var img = new Image();
      /*
       * crossOrigin MUST be set before src, or the request is made without the
       * CORS mode and the canvas is tainted anyway. Shopify's CDN answers with
       * Access-Control-Allow-Origin: *, so this succeeds for a Files-hosted
       * favicon; toDataURL is still wrapped below, because a SecurityError here
       * is a silent, permanent failure otherwise.
       */
      img.crossOrigin = 'anonymous';
      img.onload = function () { baseImage = img; resolve(img); };
      img.onerror = reject;
      img.src = cfg.src;
    });
  }

  function draw(count) {
    var canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(baseImage, 0, 0, SIZE, SIZE);

    var label = count > 9 ? '9+' : String(count);
    var r = SIZE * 0.3;
    var cx = SIZE - r - 2;
    var cy = SIZE - r - 2;

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = BADGE_BG;
    ctx.fill();
    // A ring, not decoration: it keeps the badge readable on a light favicon,
    // where pink-on-white has no edge of its own.
    ctx.lineWidth = SIZE * 0.05;
    ctx.strokeStyle = BADGE_RING;
    ctx.stroke();

    ctx.fillStyle = BADGE_FG;
    ctx.font = 'bold ' + Math.round(r * (label.length > 1 ? 1.0 : 1.25)) + 'px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx, cy + SIZE * 0.015);

    /*
     * A tainted canvas throws here — the favicon is served from an origin that
     * refused CORS. Deliberately NOT caught locally: the promise chain in
     * render() already owns every failure path (image 404, tainted canvas, no
     * 2d context) and latches `disabled`. A second, local guard was written
     * first and deleted, because a control run proved the two were
     * indistinguishable — same end state either way — so it was one more thing
     * to keep correct and nothing could ever tell if it stopped working
     * (§10.79). One mechanism, one place.
     */
    return canvas.toDataURL('image/png');
  }

  function render(count) {
    if (disabled) return;
    count = Number(count);
    if (!isFinite(count) || count < 0) return;
    if (count === lastRendered) return;

    if (count === 0) {
      if (originalHref !== null) setIcon(originalHref);
      lastRendered = 0;
      return;
    }

    loadBase()
      .then(function () {
        var url = draw(count);
        if (!url) { disabled = true; return; }
        setIcon(url);
        lastRendered = count;
      })
      .catch(function () { disabled = true; });
  }

  var inFlight = false;
  function syncFromServer() {
    if (inFlight || disabled) return;
    inFlight = true;
    var base = (cfg.cartUrl || '/cart') + '.js';
    fetch(base, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(Number(cart.item_count) || 0); })
      .catch(function () {})
      .finally(function () { inFlight = false; });
  }

  document.addEventListener('cart:count:update', function (e) {
    var count = Number(e.detail && e.detail.item_count);
    if (!isNaN(count)) render(count);
  });

  document.addEventListener('cart:update', function (e) {
    var d = e.detail || {};
    var count = (d.cart && d.cart.item_count) != null ? d.cart.item_count : d.item_count;
    if (typeof count === 'number' && !isNaN(count)) { render(count); return; }
    syncFromServer();
  });

  // A bfcache restore serves the cart count the page was rendered with, which
  // may be several adds stale if the shopper checked out in another tab.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) syncFromServer();
  });

  render(Number(cfg.count) || 0);
})();
